import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  createStyles,
  Divider,
  makeStyles,
  Theme,
  Typography,
} from '@material-ui/core';
import React, { useContext } from 'react';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import InfoIcon from '@material-ui/icons/Info';
import colors from '../../../../themes/main-colors';
import PlannedOutageIcon from '../../../outage_v2/components/icons/PlannedOutageIcon';
import clsx from 'clsx';
import { OutageStatusFAQ } from '../outage-status-faq';
import { useTranslation } from '../../../../hooks/useTranslation';
import {
  AccountPlannedOutageEvent,
  Maybe,
} from '../../../../__generated__/pge-types';
import { format } from 'date-fns';
import { enUS, es } from 'date-fns/locale';
import {
  LanguageContext,
  SupportedLanguages,
} from '../../../../providers/LanguageProvider';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    accordion: {
      backgroundColor: 'unset',
      '& .MuiAccordionSummary-root': {
        alignItems: 'start',
        padding: 0,
      },
      '& .MuiAccordionSummary-content': {
        margin: 0,
      },
      '& .MuiAccordionSummary-expandIcon': {
        right: 5,
        padding: 0,
      },
    },
    accordionDetails: {
      padding: 0,
    },
    accordionContainer: {
      padding: theme.spacing(2.5, 2.5),
      backgroundColor: colors.linenWhite,
      borderRadius: '5px',
      boxShadow: '0px 1px 1px 0px #00000040',
    },
    accordionTitle: {
      fontSize: '1.25rem',
      fontWeight: 600,
    },
    outageIcon: {
      width: 38,
      height: 38,
    },
    accordionDetailsFont: {
      fontSize: '1rem',
    },
    fontMedium: {
      fontWeight: 600,
    },
    faqSection: {
      cursor: 'pointer',
    },
    plannedOutagesListContainer: {
      display: 'flex',
      [theme.breakpoints.up('sm')]: {
        columnGap: '100px',
      },
      [theme.breakpoints.down('sm')]: {
        justifyContent: 'space-between',
      },
    },
  }),
);

export const PlannedOutageCard = ({
  plannedOutageEvents,
}: {
  plannedOutageEvents: Maybe<AccountPlannedOutageEvent>[];
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [openOutageStatusFAQ, setOpenOutageStatusFAQ] = React.useState(false);
  const { language } = useContext(LanguageContext);

  function getDate(isoDate: string) {
    const date = new Date(isoDate);
    const FORMAT = 'MMM d, yyyy';

    if (language === SupportedLanguages.English) {
      const formattedEn = format(date, FORMAT, { locale: enUS });
      return formattedEn;
    }

    if (language === SupportedLanguages.Spanish) {
      const formattedEs = format(date, FORMAT, { locale: es });
      return formattedEs;
    }
  }

  return (
    <Box className={classes.accordionContainer}>
      <Accordion elevation={0} className={classes.accordion}>
        <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="large" />}>
          <Box
            display={'flex'}
            flexDirection={'row'}
            gridGap={10}
            alignItems={'center'}
          >
            <Box>
              <PlannedOutageIcon
                className={classes.outageIcon}
                shadow="unset"
              />
            </Box>
            <Typography variant="body1" className={classes.accordionTitle}>
              {t('THERE_IS_PLANNED_OUTAGE')}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails className={classes.accordionDetails}>
          <Box display={'flex'} flexDirection={'column'} gridGap={10}>
            <Typography
              variant="body2"
              style={{ marginTop: 15, marginBottom: 15 }}
              className={classes.accordionDetailsFont}
            >
              {t('KEEP_POWER_RELIABLE')}
            </Typography>
            <Divider />
            <table>
              <tr>
                <th>
                  <Typography
                    variant="body1"
                    align="left"
                    className={clsx(
                      classes.accordionDetailsFont,
                      classes.fontMedium,
                    )}
                  >
                    {t('OUTAGE_DATE')?.toUpperCase()}
                  </Typography>
                </th>
                <th>
                  <Typography
                    variant="body1"
                    align="left"
                    className={clsx(
                      classes.accordionDetailsFont,
                      classes.fontMedium,
                    )}
                  >
                    {t('DURATION')?.toUpperCase()}
                  </Typography>
                </th>
                <th>
                  <Typography
                    variant="body1"
                    align="left"
                    className={clsx(
                      classes.accordionDetailsFont,
                      classes.fontMedium,
                    )}
                  >
                    {t('STATUS')
                      ?.split(':')?.[0]
                      ?.toUpperCase()}
                  </Typography>
                </th>
              </tr>
              {plannedOutageEvents?.map(event => (
                <>
                  <tr>
                    <td>
                      <Box>
                        <Typography
                          variant="body1"
                          className={clsx(
                            classes.accordionDetailsFont,
                            classes.fontMedium,
                          )}
                        >
                          {getDate(event?.estimatedTimeOut)}
                        </Typography>
                      </Box>
                    </td>
                    <td>
                      <Box>
                        <Typography
                          variant="body1"
                          className={clsx(
                            classes.accordionDetailsFont,
                            classes.fontMedium,
                          )}
                        >
                          {`${event?.duration}h`}
                        </Typography>
                      </Box>
                    </td>
                    <td>
                      <Box>
                        <Typography
                          variant="body1"
                          className={clsx(
                            classes.accordionDetailsFont,
                            classes.fontMedium,
                          )}
                        >
                          {t(`${event?.eventStatus}`)}
                        </Typography>
                      </Box>
                    </td>
                  </tr>
                </>
              ))}
            </table>
            <Divider />
            <Box
              display={'flex'}
              flexDirection={'row'}
              justifyContent={'space-between'}
              mt={0.75}
              className={classes.faqSection}
              onClick={() => setOpenOutageStatusFAQ(true)}
            >
              <Typography
                variant="body1"
                className={clsx(
                  classes.accordionDetailsFont,
                  classes.fontMedium,
                )}
              >
                {t('OUTAGE_FAQ')}
              </Typography>
              <InfoIcon htmlColor={colors.sparkBlue} />
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
      <OutageStatusFAQ
        open={openOutageStatusFAQ}
        setOpen={setOpenOutageStatusFAQ}
      />
    </Box>
  );
};
